import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { AreaSensivel } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { mensagemDeErro, post } from '../../nucleo/api';
import { data as dataBr, dinheiro } from '../../nucleo/formato';
import { invalidarContas, type AplicacaoCredito } from './tipos';

/**
 * Fase 2, W34: "Desfazer aplicação" na ficha da nota ou da fatura (`POST /contas/documentos/:id/desfazer-aplicacao`,
 * permissão `contas.baixar`). Os saldos das duas voltam e a aplicação fica marcada como desfeita, com o motivo
 * (obrigatório); nada é apagado. A chave de idempotência nasce com o modal.
 */
export function DesfazerAplicacao({ documentoId, aplicacao, aoFechar }: { documentoId: string | null; aplicacao: AplicacaoCredito | null; aoFechar: () => void }) {
  if (!documentoId || !aplicacao) return null;
  return <Corpo documentoId={documentoId} aplicacao={aplicacao} aoFechar={aoFechar} />;
}

function Corpo({ documentoId, aplicacao, aoFechar }: { documentoId: string; aplicacao: AplicacaoCredito; aoFechar: () => void }) {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [chave] = useState(() => crypto.randomUUID());
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const desfazer = useMutation({
    mutationFn: () => post(`/contas/documentos/${documentoId}/desfazer-aplicacao`, { aplicacao_id: aplicacao.id, motivo: motivo.trim() }, { 'Idempotency-Key': chave }),
    onSuccess: async () => {
      await invalidarContas(qc);
      toast.sucesso(t('w34.desfeita'));
      aoFechar();
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('w34.desfazerTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.voltar')}
          </Botao>
          <Botao variante="perigo" disabled={!motivo.trim()} carregando={desfazer.isPending} onClick={() => (setErro(null), desfazer.mutate())}>
            {t('w34.desfazer')}
          </Botao>
        </>
      }
    >
      <p className="mb-3 text-sm text-tinta-2" data-desfazer-aplicacao>
        {t('w34.desfazerTexto', { valor: dinheiro(aplicacao.valor_centavos, aplicacao.moeda), nota: aplicacao.nota_numero ?? '', fatura: aplicacao.fatura_numero ?? '', data: dataBr(aplicacao.data) })}
      </p>
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('w34.motivo')}
          <AreaSensivel rows={2} maxLength={300} value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco" />
        </label>
        {!motivo.trim() && <p className="text-xs text-tinta-3">{t('w34.motivoObrigatorio')}</p>}
        {erro && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
            {erro}
          </p>
        )}
      </div>
    </Modal>
  );
}
